import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getAccessCsrfToken, getRefreshCsrfToken, isStateChangingMethod } from './csrf';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

/** Token accessor — set by AuthContext after login */
let getAccessToken: () => string | null = () => sessionStorage.getItem('budgetAppToken');
let getRefreshToken: () => string | null = () => sessionStorage.getItem('budgetAppRefreshToken');
let onTokenRefreshed: ((token: string) => void) | null = null;
let onAuthFailed: (() => void) | null = null;

/** Called by AuthContext to wire up token accessors */
export function configureAuth(config: {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  onTokenRefreshed: (token: string) => void;
  onAuthFailed: () => void;
}) {
  getAccessToken = config.getAccessToken;
  getRefreshToken = config.getRefreshToken;
  onTokenRefreshed = config.onTokenRefreshed;
  onAuthFailed = config.onAuthFailed;
}

const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 120_000,
  headers: { 'Content-Type': 'application/json' },
  // Send JWT cookies (access_token_cookie / refresh_token_cookie) on every
  // request. Backend accepts JWT from cookies first, headers as fallback —
  // see app.py JWT_TOKEN_LOCATION = ['cookies', 'headers'].
  withCredentials: true,
});

// ── Request interceptor: attach Bearer token + CSRF header ──
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // /auth/refresh requires a refresh token (not an access token). The backend
  // checks headers first (JWT_TOKEN_LOCATION = ['headers', 'cookies']), so we
  // must send the right token type — otherwise it rejects with
  // "Only refresh tokens are allowed".
  const isRefresh = config.url?.includes('/auth/refresh');
  if (isRefresh) {
    const refreshTok = getRefreshToken();
    if (refreshTok) {
      config.headers.Authorization = `Bearer ${refreshTok}`;
    }
  } else {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Echo the CSRF cookie value on state-changing requests so the backend's
  // JWT_COOKIE_CSRF_PROTECT double-submit check passes. /auth/refresh uses a
  // different cookie (csrf_refresh_token) than every other authed endpoint.
  if (isStateChangingMethod(config.method)) {
    const csrf = isRefresh ? getRefreshCsrfToken() : getAccessCsrfToken();
    if (csrf) {
      config.headers['X-CSRF-TOKEN'] = csrf;
    }
  }

  // Don't override Content-Type for FormData (let browser set multipart boundary)
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// ── Response interceptor: 401 → refresh → retry once ──
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

// Timestamp of the last refresh attempt triggered by a network/CORS error
// (as opposed to an explicit 401).  Used to rate-limit refresh calls when
// the backend is genuinely down: if the refresh itself failed recently we
// skip the next wave of network-error-triggered refreshes instead of
// hammering /auth/refresh during an outage.
let lastNetworkRefreshAttemptMs = 0;
const NETWORK_REFRESH_COOLDOWN_MS = 10_000; // 10 s between network-error refresh attempts

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // A CORS-blocked response arrives in axios as a network error with
    // error.response === undefined (the browser never delivers the body).
    // This happens when the backend returns a 401/5xx without CORS headers —
    // e.g. during token expiry or a brief backend outage.  We treat it the
    // same as an explicit 401 so the token-refresh path is still attempted.
    const is401 = error.response?.status === 401;
    const isNetworkError = !error.response; // covers CORS-blocked responses too

    if (originalRequest._retry || (!is401 && !isNetworkError)) {
      return Promise.reject(error);
    }

    const reqUrl = originalRequest.url ?? '';

    // Skip refresh for the refresh endpoint itself — don't logout,
    // just reject. The AuthContext mount retry handles this gracefully.
    if (reqUrl.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    // Failed password / SSO login returns 401 with a real error body — not an
    // expired session. Retrying refresh here replaces it with "No refresh token".
    if (
      reqUrl.includes('/auth/login') ||
      reqUrl.includes('/auth/microsoft') ||
      reqUrl.includes('/auth/local-admin/login')
    ) {
      return Promise.reject(error);
    }

    if (isNetworkError) {
      // Gate network-error refresh attempts with two guards:
      //
      // 1. Must have a refresh token — if there's nothing to refresh with,
      //    this is definitely not a CORS-blocked 401.
      //
      // 2. Cooldown — if the last network-error-triggered refresh attempt
      //    was less than 10 s ago we skip immediately.  This prevents
      //    hammering /auth/refresh during a genuine backend outage: the
      //    refresh POST would also return a 502/503, processQueue would
      //    reject everything, and React components re-mounting would send
      //    the whole cycle again every render cycle.
      if (!getRefreshToken()) {
        return Promise.reject(error);
      }
      const now = Date.now();
      if (now - lastNetworkRefreshAttemptMs < NETWORK_REFRESH_COOLDOWN_MS) {
        return Promise.reject(error);
      }
      lastNetworkRefreshAttemptMs = now;
    }

    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            // Refresh also rotates the CSRF cookie — update the header so the
            // retried request passes the backend's double-submit CSRF check.
            if (isStateChangingMethod(originalRequest.method)) {
              const newCsrf = getAccessCsrfToken();
              if (newCsrf) originalRequest.headers['X-CSRF-TOKEN'] = newCsrf;
            }
            resolve(apiClient(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token');

      const { data } = await axios.post(`${API_BASE}/auth/refresh`, null, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'X-CSRF-TOKEN': getRefreshCsrfToken(),
        },
        withCredentials: true,
      });

      const newToken = data.access_token;
      onTokenRefreshed?.(newToken);
      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Don't auto-logout — the access token from sessionStorage may still work
      // for subsequent requests. Only logout when the user explicitly gets
      // redirected to login (e.g. via ProtectedRoute check).
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
export { API_BASE };
